import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import type { AuthorizationContextValue } from "./authorization-context";
import {
  evaluatePolicyInput,
  type PolicyEvaluationInput,
} from "./policy-evaluator";

/**
 * 認可判定に利用する汎用メッセージを集約した定数です。
 * 各キーは `PolicyResult` の `reason` と同値となる想定で、UI 側で共通利用できるように Lingui の `msg`
 * マクロを用いて宣言します。
 */
export const DENY_MESSAGE = msg`You do not have permission to perform this action.`;
export const SELF_OPERATION_DENY_MESSAGE = msg`You cannot perform this action on yourself.`;

/**
 * 任意のポリシー関数が返す標準化された判定結果を表します。
 * `ok` が `false` の場合は、`reason` を利用してエラー理由を示します。
 */
export type PolicyResult = { ok: true } | PolicyFailure;

/**
 * ポリシーの失敗時に返却する情報を表します。
 * `reason` は Lingui の `MessageDescriptor` であり、UI から再利用してトーストやツールチップに表示できます。
 */
type PolicyFailure = {
  ok: false;
  reason: MessageDescriptor;
};

export type PolicyEvaluator<TContext> = (context: TContext) => PolicyResult;

type PolicyEvaluatorWithArgs<TContext, TArgs> = (
  context: TContext,
  args: TArgs,
) => PolicyResult;

/**
 * 引数なしで評価できるポリシー定義です。`run` がそのまま評価関数になります。
 */
export type StaticPolicyDefinition<TContext> = {
  run: PolicyEvaluator<TContext>;
  build?: never;
};

/**
 * 引数を受け取ってから評価関数を生成するポリシー定義です。
 * `checkPolicy` 側で引数を渡し忘れると安全側で `deny()` が返されます。
 */
export type FactoryPolicyDefinition<TContext, TArgs> = {
  build(args: TArgs): PolicyEvaluator<TContext>;
  run?: never;
} & ((args: TArgs) => StaticPolicyDefinition<TContext>);

/**
 * すべてのポリシー定義が満たす共通インターフェースです。
 * `createPolicy` で生成された値はこの型のどちらかのバリアントになります。
 */
export type PolicyDefinition<TContext, TArgs = unknown> =
  | StaticPolicyDefinition<TContext>
  | FactoryPolicyDefinition<TContext, TArgs>;

function takesOnlyContext<TContext, TArgs>(
  evaluator:
    | PolicyEvaluator<TContext>
    | PolicyEvaluatorWithArgs<TContext, TArgs>,
): evaluator is PolicyEvaluator<TContext> {
  return evaluator.length <= 1;
}

function isFactoryPolicyDefinition<TContext, TArgs>(
  policy: PolicyDefinition<TContext, TArgs>,
): policy is FactoryPolicyDefinition<TContext, TArgs> {
  return typeof policy === "function" && "build" in policy;
}

/**
 * 成功を表す `PolicyResult` を生成します。
 */
export function allow(): PolicyResult {
  return { ok: true };
}

/**
 * 失敗を表す `PolicyResult` を生成します。
 * @param reason エラー理由を表す識別子。
 */
export function deny(reason = DENY_MESSAGE): PolicyResult {
  return { ok: false, reason };
}

/**
 * 自分自身に対する操作を拒否する定型レスポンスです。
 */
export function denySelfOperation(): PolicyResult {
  return deny(SELF_OPERATION_DENY_MESSAGE);
}

export function createPolicy(
  evaluator: (context: AuthorizationContextValue) => PolicyResult,
): StaticPolicyDefinition<AuthorizationContextValue>;
export function createPolicy<TArgs>(
  evaluator: (context: AuthorizationContextValue, args: TArgs) => PolicyResult,
): FactoryPolicyDefinition<AuthorizationContextValue, TArgs>;
export function createPolicy<TContext>(
  evaluator: (context: TContext) => PolicyResult,
): StaticPolicyDefinition<TContext>;
export function createPolicy<TContext, TArgs>(
  evaluator: (context: TContext, args: TArgs) => PolicyResult,
): FactoryPolicyDefinition<TContext, TArgs>;

/**
 * 任意の判定ロジックから再利用可能なポリシーを生成します。
 */
export function createPolicy<TContext, TArgs>(
  evaluator:
    | PolicyEvaluator<TContext>
    | PolicyEvaluatorWithArgs<TContext, TArgs>,
): PolicyDefinition<TContext, TArgs> {
  if (takesOnlyContext(evaluator)) {
    return { run: evaluator };
  }

  const factory = Object.assign(
    (args: TArgs) =>
      createPolicy((context: TContext) => evaluator(context, args)),
    {
      build: (args: TArgs) => (context: TContext) => evaluator(context, args),
    },
  );

  return factory as FactoryPolicyDefinition<TContext, TArgs>;
}

export type PolicyConfigValue =
  | PolicyEvaluationInput
  | ((context: AuthorizationContextValue) => PolicyEvaluationInput);

/**
 * 複数のポリシーをまとめて評価し、真偽値のマップを返します。
 *
 * @param context - 現在の認可コンテキスト。
 * @param config - 判定したいポリシー定義をプロパティ名とセットで与えます。
 */
export type CheckPoliciesResult<T extends Record<string, PolicyConfigValue>> = {
  [K in keyof T]: PolicyResult;
};

export function checkPolicies<T extends Record<string, PolicyConfigValue>>(
  context: AuthorizationContextValue,
  config: T,
): CheckPoliciesResult<T> {
  return Object.fromEntries(
    Object.entries(config).map(([key, value]) => {
      const input = typeof value === "function" ? value(context) : value;
      return [key, evaluatePolicyInput(input, context)];
    }),
  ) as CheckPoliciesResult<T>;
}

export function checkPolicy<TContext, TArgs>(
  policy: PolicyDefinition<TContext, TArgs>,
  context: TContext,
): PolicyResult;
export function checkPolicy<TContext, TArgs>(
  policy: PolicyDefinition<TContext, TArgs>,
  context: TContext,
  args: TArgs,
): PolicyResult;

/**
 * ポリシー定義を評価し、`PolicyResult` を返します。
 * UI やサーバー処理から直接呼び出す場合はこちらを利用してください。
 * 引数付きポリシーで `args` を省略すると安全側（deny）で応答します。
 */
export function checkPolicy<TContext, TArgs = unknown>(
  policy: PolicyDefinition<TContext, TArgs>,
  context: TContext,
  args?: TArgs,
): PolicyResult {
  if (isFactoryPolicyDefinition(policy)) {
    if (args === undefined) {
      // 引数が必須のポリシーに誤って空引数で到達した場合は拒否判定で早期終了させる。
      return deny();
    }
    return policy.build(args)(context);
  }

  return policy.run(context);
}
